import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Activity run renderer (fn-48.3, R4) — friendly one-liners with an
// expandable technical disclosure.
//
// Default render is COPY-MODULE ONLY: the friendly line of the
// current step while the run is live, a single summary line once the
// turn completes ("Worked on N steps"; single steps keep their own
// line). Raw tool names, args JSON, and outputs render ONLY inside
// the disclosure — operator-grade detail, opt-in via "Show details".
//
// HITL decisions reflect quietly: a small note on the line (and the
// per-step detail), never a banner.
import { useState } from 'react';
import { HIDE_DETAILS, SHOW_DETAILS, decisionNote, durationSuffix, workedOnSteps, } from '../copy/copy.js';
function headlineFor(steps, live) {
    const lastStep = steps[steps.length - 1];
    if (live && lastStep !== undefined)
        return lastStep.friendly;
    if (steps.length === 1 && lastStep !== undefined)
        return lastStep.friendly;
    return workedOnSteps(steps.length);
}
/** The latest decision across the run, for the quiet inline note. */
function lastDecision(steps) {
    for (let i = steps.length - 1; i >= 0; i -= 1) {
        const decision = steps[i]?.decision ?? null;
        if (decision !== null)
            return decision;
    }
    return null;
}
/**
 * The MCP server name to tag on the headline (fn-67) — the latest MCP
 * step's server, mirroring `lastDecision`. Null when the run holds no
 * MCP step (built-in / custom runs render no tag). Default-visible so
 * an approver sees the server before expanding the disclosure.
 */
function headlineServerName(steps) {
    for (let i = steps.length - 1; i >= 0; i -= 1) {
        const name = steps[i]?.mcpServerName ?? null;
        if (name !== null && name.length > 0)
            return name;
    }
    return null;
}
export function ActivityBlock(props) {
    const [open, setOpen] = useState(false);
    const headline = headlineFor(props.steps, props.live);
    // Duration renders as its OWN node so it can be styled muted
    // independently of the headline (the "quiet" contract) — never
    // while live.
    const duration = props.live ? null : (props.duration ?? null);
    const decision = lastDecision(props.steps);
    const serverName = headlineServerName(props.steps);
    return (_jsxs("div", { className: `sbui-chat-activity${props.live ? ' sbui-chat-activity--live' : ''}`, children: [_jsxs("div", { className: "sbui-chat-activity-line", children: [_jsx("span", { className: "sbui-chat-activity-text", children: headline }), serverName !== null && (_jsx("span", { className: "sbui-chat-activity-server", title: `MCP server: ${serverName}`, children: serverName })), duration !== null && (_jsx("span", { className: "sbui-chat-activity-duration", children: durationSuffix(duration) })), decision !== null && (_jsx("span", { className: "sbui-chat-activity-decision", children: decisionNote(decision) })), _jsxs("button", { type: "button", className: "sbui-chat-activity-toggle", "aria-expanded": open, onClick: () => setOpen((v) => !v), children: [_jsx("span", { className: "sbui-chat-activity-chevron", "aria-hidden": "true" }), open ? HIDE_DETAILS : SHOW_DETAILS] })] }), open && (_jsx("ul", { className: "sbui-chat-activity-detail", children: props.steps.map((step) => (_jsxs("li", { className: "sbui-chat-activity-step", children: [step.rawToolName !== null && (_jsx("code", { className: "sbui-chat-activity-tool", children: step.rawToolName })), step.decision !== null && (_jsxs("p", { className: "sbui-chat-activity-step-decision", children: [decisionNote(step.decision), step.denyMessage !== null && step.denyMessage.length > 0
                                    ? ` — ${step.denyMessage}`
                                    : ''] })), step.input !== null && (_jsx("pre", { className: "sbui-chat-activity-io", children: JSON.stringify(step.input, null, 2) })), step.output !== null && (_jsx("pre", { className: `sbui-chat-activity-io${step.isError ? ' sbui-chat-activity-io--error' : ''}`, children: step.output }))] }, `step-${step.id}`))) }))] }));
}
//# sourceMappingURL=activity.js.map